'use client';

import {
    CalcContext,
    CalculatorContextT,
    FetcherContext,
    OptimizerContextProvider,
    OptimizerProgress,
} from '@copmer/calculator-integration/client';
import { ReactNode } from 'react';

import { CalculatorCopyContextProvider } from '@/freight/components/copy-context';
import { CalculatorCurrencyProvider } from '@/freight/components/currency-context';

import { reactQueryFetcher } from './react-query-fetcher';

export interface CalculatorProviderProps {
    children: ReactNode;
    /**
     * The CalcContext value from useCalculatorContextProvideValue hook.
     * Pages can continue using their existing pattern to create this value.
     */
    calcContextValue?: CalculatorContextT & {
        isCFROptimizer?: boolean;
        onOptimizerProgress?: (progress: OptimizerProgress | null) => void;
    };
    /**
     * Initial currency for the currency provider
     */
    initialCurrency?: string;
    /**
     * Skip the CalcContext provider (useful if you need custom CalcContext handling)
     */
    skipCalcContext?: boolean;
    /**
     * Skip the currency provider
     */
    skipCurrencyContext?: boolean;
    /**
     * Skip the copy context provider
     */
    skipCopyContext?: boolean;
}

/**
 * Unified provider component that supplies all calculator-related contexts.
 *
 * This provider encapsulates:
 * - FetcherContext (from @copmer/calculator-integration/client) - for react-query based data fetching
 * - CalcContext (from @copmer/calculator-integration/client) - for calculator state and routing (when value provided)
 * - CalculatorCurrencyProvider - for currency conversion
 * - CalculatorCopyContextProvider - for copy functionality
 *
 * This provider should be placed inside a QueryClientProvider (from @tanstack/react-query).
 *
 * @example Basic usage with CalcContext value from hook
 * ```tsx
 * import { CalculatorProvider } from '@/lib/calculator/calculator-provider';
 * import { useCalculatorContextProvideValue } from '@/freight/components/context';
 *
 * function FreightPage() {
 *   const calcContextValue = useCalculatorContextProvideValue();
 *
 *   return (
 *     <CalculatorProvider calcContextValue={calcContextValue} initialCurrency="USD">
 *       <CalculatorWidget />
 *     </CalculatorProvider>
 *   );
 * }
 * ```
 *
 * @example Skip certain contexts if you need custom handling
 * ```tsx
 * <CalculatorProvider skipCalcContext skipCurrencyContext>
 *   <CustomCalcContextProvider>
 *     <CalculatorWidget />
 *   </CustomCalcContextProvider>
 * </CalculatorProvider>
 * ```
 */
export function CalculatorProvider({
    children,
    calcContextValue,
    initialCurrency,
    skipCalcContext = false,
    skipCurrencyContext = false,
    skipCopyContext = false,
}: CalculatorProviderProps) {
    // Build the provider tree from innermost to outermost
    let content = children;

    // Innermost: Copy context
    if (!skipCopyContext) {
        content = <CalculatorCopyContextProvider>{content}</CalculatorCopyContextProvider>;
    }

    // Currency context
    if (!skipCurrencyContext) {
        content = <CalculatorCurrencyProvider initialCurrency={initialCurrency}>{content}</CalculatorCurrencyProvider>;
    }

    // CalcContext from calculator-widget (only if value provided and not skipped)
    if (!skipCalcContext && calcContextValue) {
        content = (
            <CalcContext.Provider value={calcContextValue}>
                <OptimizerContextProvider
                    isCFROptimizer={calcContextValue.isCFROptimizer ?? false}
                    onOptimizerProgress={calcContextValue.onOptimizerProgress}
                    initialAllowToleranceOverride={true}
                >
                    {content}
                </OptimizerContextProvider>
            </CalcContext.Provider>
        );
    }

    // FetcherContext from calculator-ui (always included)
    content = <FetcherContext.Provider value={reactQueryFetcher}>{content}</FetcherContext.Provider>;

    return <>{content}</>;
}

/**
 * Minimal provider that only includes the FetcherContext.
 * Use this when you only need react-query fetching without other calculator contexts.
 */
export function FetcherProvider({ children }: { children: ReactNode }) {
    return <FetcherContext.Provider value={reactQueryFetcher}>{children}</FetcherContext.Provider>;
}
